/**
 * Notification Polling Service
 *
 * This service provides a fallback mechanism for receiving critical notifications
 * when WebSocket connections are unstable (common on Windows due to tab throttling,
 * VPN, power management, etc.).
 *
 * Flow:
 * 1. When WebSocket disconnects, start polling
 * 2. Poll for pending notifications
 * 3. Process received notifications like WebSocket messages
 * 4. Stop polling when WebSocket reconnects
 */

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Track polling state per session type
const pollingState = {
  guestSession: { active: false, intervalId: null },
  userSession: { active: false, intervalId: null },
  subUserSession: { active: false, intervalId: null },
  notarySession: { active: false, intervalId: null },
};

// Default polling interval (3 seconds)
const DEFAULT_POLL_INTERVAL = 3000;

/**
 * Start polling for pending notifications
 * @param {string} sessionType - The session type (guestSession, userSession, etc.)
 * @param {string} recipientId - The recipient ID (guest_id, user_id, etc.)
 * @param {function} onNotification - Callback for each received notification
 * @param {number} interval - Polling interval in milliseconds
 */
export const startPolling = (
  sessionType,
  recipientId,
  onNotification,
  interval = DEFAULT_POLL_INTERVAL,
) => {
  // Don't start if already polling
  if (pollingState[sessionType]?.active) {
    return;
  }

  pollingState[sessionType] = {
    active: true,
    intervalId: setInterval(async () => {
      try {
        const notifications = await fetchPendingNotifications(
          sessionType,
          recipientId,
        );

        if (notifications && notifications.length > 0) {
          notifications.forEach((notification) => {
            if (onNotification) {
              onNotification({
                type: sessionType,
                event: notification.event,
                data: notification.payload,
                fromPolling: true,
              });
            }
          });
        }
      } catch (error) {
        console.error(`Polling error for ${sessionType}:`, error);
      }
    }, interval),
  };
};

/**
 * Stop polling for a session type
 * @param {string} sessionType - The session type to stop polling
 */
export const stopPolling = (sessionType) => {
  if (pollingState[sessionType]?.active) {
    clearInterval(pollingState[sessionType].intervalId);
    pollingState[sessionType] = { active: false, intervalId: null };
  }
};

/**
 * Stop all polling
 */
export const stopAllPolling = () => {
  Object.keys(pollingState).forEach((sessionType) => {
    stopPolling(sessionType);
  });
};

/**
 * Check if polling is active for a session type
 * @param {string} sessionType - The session type to check
 * @returns {boolean}
 */
export const isPollingActive = (sessionType) => {
  return pollingState[sessionType]?.active || false;
};

/**
 * Fetch pending notifications from the server
 * @param {string} sessionType - The session type
 * @param {string} recipientId - The recipient ID
 * @returns {Promise<Array>} Array of pending notifications
 */
export const fetchPendingNotifications = async (sessionType, recipientId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/nocApi/notification/poll-pending`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          currentSession: sessionType,
          recipientId: recipientId,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      return data.notifications || [];
    }

    return [];
  } catch (error) {
    console.error("Error fetching pending notifications:", error);
    return [];
  }
};

/**
 * Check if there are pending notifications (lightweight check)
 * @param {string} sessionType - The session type
 * @param {string} recipientId - The recipient ID
 * @returns {Promise<{hasPending: boolean, count: number}>}
 */
export const checkPendingCount = async (sessionType, recipientId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/nocApi/notification/check-pending-count`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          currentSession: sessionType,
          recipientId: recipientId,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      hasPending: data.hasPending || false,
      count: data.count || 0,
    };
  } catch (error) {
    console.error("Error checking pending count:", error);
    return { hasPending: false, count: 0 };
  }
};

/**
 * One-time fetch and process of pending notifications
 * Useful for when WebSocket reconnects to catch up on missed messages
 * @param {string} sessionType - The session type
 * @param {string} recipientId - The recipient ID
 * @param {function} onNotification - Callback for each notification
 */
export const catchUpMissedNotifications = async (
  sessionType,
  recipientId,
  onNotification,
) => {
  try {
    const notifications = await fetchPendingNotifications(
      sessionType,
      recipientId,
    );

    if (notifications && notifications.length > 0) {
      notifications.forEach((notification) => {
        if (onNotification) {
          onNotification({
            type: sessionType,
            event: notification.event,
            data: notification.payload,
            fromPolling: true,
            catchUp: true,
          });
        }
      });
    }
  } catch (error) {
    console.error("Error catching up missed notifications:", error);
  }
};

const notificationPolling = {
  startPolling,
  stopPolling,
  stopAllPolling,
  isPollingActive,
  fetchPendingNotifications,
  checkPendingCount,
  catchUpMissedNotifications,
};

export default notificationPolling;
